import "@maersk-global/mds-components-core-toast";
import type { McToast } from "@maersk-global/mds-components-core-toast";
import type { McNotification } from "@maersk-global/mds-components-core-notification";

export interface Phase4FeedbackContract {
  show: (type: "success" | "error", message: string, durationMs?: number) => void;
  clear: () => void;
}

export function installPhase4Feedback(documentRef: Document): Phase4FeedbackContract | undefined {
  const legacyAlert = documentRef.getElementById("alertBox");
  if (!legacyAlert) return undefined;

  // Preserve the legacy status ID without announcing the same message twice.
  // MDS mounts its live toast container on body, outside main's layout containment.
  documentRef.body.appendChild(legacyAlert);
  legacyAlert.classList.add("hidden");
  legacyAlert.setAttribute("aria-hidden", "true");
  legacyAlert.removeAttribute("role");
  legacyAlert.removeAttribute("aria-live");

  let activeToast: McToast | null = null;

  const clear = (): void => {
    const previous = activeToast;
    activeToast = null;
    legacyAlert.textContent = "";
    previous?.hide();
    previous?.remove();
  };

  const show: Phase4FeedbackContract["show"] = (type, message, durationMs) => {
    clear();
    legacyAlert.textContent = message;

    const toast = documentRef.createElement("mc-toast") as McToast;
    toast.classList.add("phase4-feedback-toast");
    toast.position = "top-right";
    toast.appearance = type;
    toast.duration = durationMs ?? (type === "success" ? 7000 : 9000);

    const notification = documentRef.createElement("mc-notification") as McNotification;
    notification.classList.add("phase4-feedback-notification");
    notification.icon = type === "success" ? "check-circle" : "exclamation-octagon";
    notification.textContent = message;
    notification.labels = { close: documentRef.documentElement.lang.startsWith("pt") ? "Fechar" : "Close" };
    toast.appendChild(notification);
    activeToast = toast;

    toast.addEventListener("close", () => {
      if (activeToast === toast) {
        activeToast = null;
        legacyAlert.textContent = "";
      }
      toast.remove();
    }, { once: true });
    documentRef.body.appendChild(toast);

    // Wait for slot assignment; a newer message can supersede this one meanwhile.
    void toast.updateComplete.then(() => {
      if (activeToast === toast) toast.show();
    });
  };

  return { show, clear };
}
